import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { Button, Typography, Space, Alert, Spin } from 'antd';
import { LoginOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import IntroVideo from '../components/common/IntroVideo';
import useIntroVideo from '../hooks/useIntroVideo';
import AnimatedBackground from '../components/common/AnimatedBackground';

// The login screen is always dark, so pin the white-on-dark wordmark
// rather than following the theme mode (see theme/csgLogo.js).
import { CSG_LOGO } from '../theme/csgLogo';

const { Text } = Typography;

// Keyframe Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeInScale = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const pulseGlow = keyframes`
  0%, 100% {
    filter: drop-shadow(0 0 20px rgba(141, 215, 247, 0.4))
            drop-shadow(0 0 40px rgba(141, 215, 247, 0.2));
  }
  50% {
    filter: drop-shadow(0 0 30px rgba(141, 215, 247, 0.6))
            drop-shadow(0 0 60px rgba(141, 215, 247, 0.3));
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  position: relative;
  z-index: 1;
`;

const LoginCard = styled.div`
  background: rgba(26, 26, 46, 0.85);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(141, 215, 247, 0.2);
  border-radius: 20px;
  padding: 48px 40px;
  text-align: center;
  max-width: 420px;
  width: 100%;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.4),
    0 0 80px rgba(141, 215, 247, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  animation: ${fadeInScale} 0.6s ease-out forwards;

  /* Subtle top highlight */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 20%;
    right: 20%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(141, 215, 247, 0.5),
      transparent
    );
  }

  @media (max-width: 480px) {
    padding: 32px 24px;
    margin: 0 16px;
  }
`;

const LogoContainer = styled.div`
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
  animation: ${fadeInUp} 0.6s ease-out 0.2s both;
`;

const LogoImage = styled.picture`
  display: block;
  animation: ${float} 6s ease-in-out infinite;

  img {
    max-width: 220px;
    height: auto;
    animation: ${pulseGlow} 4s ease-in-out infinite;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
    img {
      animation: none;
      filter: drop-shadow(0 0 25px rgba(141, 215, 247, 0.5));
    }
  }
`;

const Title = styled.h1`
  font-family: 'Orbitron', sans-serif;
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0 0 8px 0;
  letter-spacing: 2px;
  text-shadow: 0 0 20px rgba(141, 215, 247, 0.5);
  animation: ${fadeInUp} 0.6s ease-out 0.3s both;

  @media (max-width: 480px) {
    font-size: 1.5rem;
    letter-spacing: 1px;
  }
`;

const Subtitle = styled.p`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.95rem;
  margin: 0;
  letter-spacing: 1px;
  animation: ${fadeInUp} 0.6s ease-out 0.4s both;
`;

const ButtonContainer = styled.div`
  margin-top: 32px;
  animation: ${fadeInUp} 0.6s ease-out 0.5s both;
`;

const LoginButton = styled(Button)`
  &.ant-btn {
    height: 52px;
    font-size: 16px;
    font-weight: 500;
    border: none;
    border-radius: 12px;
    background: linear-gradient(135deg, #4a9eff 0%, #8dd7f7 50%, #4a9eff 100%);
    background-size: 200% auto;
    box-shadow:
      0 4px 20px rgba(141, 215, 247, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.2);
    transition: all 0.3s ease;
    position: relative;
    overflow: hidden;

    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow:
        0 8px 30px rgba(141, 215, 247, 0.4),
        inset 0 1px 0 rgba(255, 255, 255, 0.2);
      animation: ${shimmer} 2s linear infinite;
    }

    &:active:not(:disabled) {
      transform: translateY(0);
      box-shadow:
        0 2px 10px rgba(141, 215, 247, 0.3),
        inset 0 2px 4px rgba(0, 0, 0, 0.2);
    }

    &:disabled {
      background: linear-gradient(135deg, #4a9eff 0%, #8dd7f7 100%);
      opacity: 0.7;
    }

    .anticon {
      font-size: 18px;
    }
  }
`;

const FooterText = styled.p`
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
  margin: 24px 0 0 0;
  letter-spacing: 0.5px;
  animation: ${fadeInUp} 0.6s ease-out 0.6s both;
`;

const ErrorAlert = styled(Alert)`
  margin-top: 20px;
  text-align: left;
  border-radius: 8px;
  animation: ${fadeInUp} 0.3s ease-out forwards;
`;

const DevAlert = styled(Alert)`
  margin-top: 16px;
  text-align: left;
  border-radius: 8px;
`;

const LoadingCard = styled(LoginCard)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 16px;
`;

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, error, isInitialized } = useAuth();
  const { showIntro, triggerIntro, markIntroPlayed, shouldShowIntro } = useIntroVideo();
  const [pendingNavigation, setPendingNavigation] = useState(null);

  // Handle navigation after auth (with optional video)
  useEffect(() => {
    if (isAuthenticated) {
      const targetPath = location.state?.from?.pathname || '/';
      setPendingNavigation(targetPath);

      // Check if we should show the intro video
      if (shouldShowIntro()) {
        triggerIntro();
      } else {
        // No video needed, navigate immediately
        navigate(targetPath, { replace: true });
      }
    }
  }, [isAuthenticated, navigate, location, shouldShowIntro, triggerIntro]);

  // Handle video completion
  const handleVideoComplete = useCallback(() => {
    markIntroPlayed();
    if (pendingNavigation) {
      navigate(pendingNavigation, { replace: true });
    }
  }, [markIntroPlayed, navigate, pendingNavigation]);

  const handleLogin = async () => {
    try {
      await login();
      // Navigation will happen via the useEffect above
    } catch (err) {
      console.error('Login error:', err);
      // Error is handled by AuthContext
    }
  };

  // Show intro video if triggered
  if (showIntro) {
    return <IntroVideo onComplete={handleVideoComplete} />;
  }

  // Show loading while checking auth status
  if (!isInitialized) {
    return (
      <>
        <AnimatedBackground />
        <PageContainer>
          <LoadingCard>
            <Spin size="large" />
            <Text style={{ color: 'rgba(255,255,255,0.6)' }}>
              Initializing...
            </Text>
          </LoadingCard>
        </PageContainer>
      </>
    );
  }

  return (
    <>
      <AnimatedBackground />
      <PageContainer>
        <LoginCard>
          <LogoContainer>
            <LogoImage>
              <source srcSet={CSG_LOGO.dark.webp} type="image/webp" />
              <img src={CSG_LOGO.dark.png} alt="CSG" />
            </LogoImage>
          </LogoContainer>

          <Subtitle>Unified MSP Management Platform</Subtitle>

          {error && (
            <ErrorAlert
              type="error"
              message="Authentication Error"
              description={error.message || 'Failed to sign in. Please try again.'}
              showIcon
            />
          )}

          <ButtonContainer>
            <LoginButton
              type="primary"
              size="large"
              icon={<LoginOutlined />}
              onClick={handleLogin}
              loading={isLoading}
              disabled={isLoading}
              block
            >
              {isLoading ? 'Signing in...' : 'Sign in with Microsoft'}
            </LoginButton>
          </ButtonContainer>

          <FooterText>
            Secure authentication via Microsoft Entra ID
          </FooterText>

          {import.meta.env.DEV && !import.meta.env.VITE_ENTRA_CLIENT_ID && (
            <DevAlert
              type="warning"
              message="Development Mode"
              description="VITE_ENTRA_CLIENT_ID not configured. Set up .env file for authentication."
              showIcon
            />
          )}
        </LoginCard>
      </PageContainer>
    </>
  );
};

export default LoginPage;
